// frontend/src/pages/admin/LearningDevelopment/components/CertComplianceTab.js
// Mandatory Certification compliance engine UI:
// rule management + per-rule live compliance dashboard with employee drill-down.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, Plus, Pencil, Trash2,
  ChevronRight, CheckCircle, X, RefreshCw, Scale, GraduationCap, UserPlus,
  ExternalLink,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from '../../../../utils/toast';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { CountBadge } from '../../../../components/UI/CountBadge';
import { InfoBadge } from '../../../../components/UI/InfoBadge';
import { TradeBadge } from '../../../../components/UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../../../../config/tradeConfig';
import { ClassificationBadge } from '../../../../components/UI/ClassificationBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import IconButton from '../../../../components/UI/IconButton';
import Portal from '../../../../components/UI/Portal';
import { getDepartmentBadgeColor } from '../../../../config/departmentConfig';
import {
  useMandatoryCertRules,
  useMandatoryCertComplianceDashboard,
  useSafetyCertificationTypes,
  useCreateMandatoryCertRule,
  useUpdateMandatoryCertRule,
  useDeleteMandatoryCertRule,
} from '../../../../hooks/useMandatoryCertCompliance';
import { useDepartments, useTradeCategories, useDistinctDesignations } from '../../../../hooks/useConfigData';
import { formatDate as formatDateCanonical, parseLocalDate } from '../../../../utils/dateUtils';

/** Deep-link into self-service New Request with worker + library course prefilled. */
const buildNominateUrl = (staffId, courseCode) => {
  const params = new URLSearchParams({
    nominate: '1',
    applicant: staffId,
    master_course: courseCode,
  });
  return `/self-service/course-requests?${params.toString()}`;
};

/** Open an existing course request in the admin L&D workqueue. */
const buildAdminCourseRequestUrl = (requestId) => (
  `/admin/learning-development?tab=course-requests&id=${encodeURIComponent(requestId)}`
);

const CLASSIFICATION_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'worker', label: 'Worker' },
  { value: 'intern', label: 'Intern' },
  { value: 'managing_director', label: 'Managing Director' },
  { value: 'executive_director', label: 'Executive Director' },
];

const STATUS_CONFIG = {
  expired: { label: 'Expired', badge: 'expired' },
  missing: { label: 'Missing', badge: 'rejected' },
  no_expiry_date: { label: 'No expiry date', badge: 'warning' },
  within_30: { label: '≤30 days left', badge: 'expiring_soon' },
  within_60: { label: '31–60 days left', badge: 'expiring_soon' },
  within_90: { label: '61–90 days left', badge: 'pending' },
  in_grace: { label: 'New-hire grace', badge: 'in_progress' },
};

/** Date-only chip label; null when missing/invalid (DS: dateUtils only). */
const formatDate = (value) => {
  if (!value) return null;
  const localDate = parseLocalDate(String(value).split('T')[0]);
  if (!localDate) return null;
  const formatted = formatDateCanonical(localDate);
  return formatted === 'N/A' || formatted === 'Invalid Date' ? null : formatted;
};

/** Strip trailing "(CSOC)" / "(BCSS)" from catalog names — show WSQ title first. */
const wsqDisplayName = (certName) => {
  if (!certName) return '';
  return certName.replace(/\s*\([A-Z0-9]+\)\s*$/, '').trim();
};

const classificationLabel = (value) =>
  CLASSIFICATION_OPTIONS.find((o) => o.value === value)?.label || value;

// Structured scope chips are the single source of truth for who a rule covers.
// Prefer the live dashboard payload (result.scope); fall back to the raw rule
// record (department_details / trade_details) for inactive rules that aren't
// evaluated. This replaces the old name-parsed scope label, which both
// duplicated these chips on the card and silently drifted whenever scope was
// edited without also renaming the rule.
const getScopeChips = (result, rule) => {
  if (result?.scope) {
    return [
      ...(result.scope.departments || []).map((label) => ({ type: 'department', label })),
      ...(result.scope.trades || []).map((label) => ({ type: 'trade', label })),
      ...(result.scope.designations || []).map((label) => ({ type: 'designation', label })),
      ...(result.scope.designation_keywords || []).map((label) => ({
        type: 'designation_keyword',
        label: `${label}*`,
      })),
      ...(result.scope.classifications || []).map((value) => ({
        type: 'classification',
        label: classificationLabel(value),
        value,
      })),
    ];
  }
  return [
    ...(rule?.department_details || []).map((d) => ({ type: 'department', label: d.department_name })),
    ...(rule?.trade_details || []).map((t) => ({
      type: 'trade',
      label: t.trade_code || t.name,
      code: t.trade_code,
      name: t.name,
    })),
    ...(rule?.applies_to_designations || []).map((label) => ({ type: 'designation', label })),
    ...(rule?.applies_to_designation_keywords || []).map((label) => ({
      type: 'designation_keyword',
      label: `${label}*`,
    })),
    ...(rule?.applies_to_classifications || []).map((value) => ({
      type: 'classification',
      label: classificationLabel(value),
      value,
    })),
  ];
};

const ScopeChip = ({ chip, size }) => {
  if (chip.type === 'department') {
    return (
      <InfoBadge
        color={getDepartmentBadgeColor(chip.label)}
        label={chip.label}
        size={size}
      />
    );
  }
  if (chip.type === 'classification') {
    return (
      <ClassificationBadge
        classification={chip.value}
        label={chip.label}
        size={size === 'xs' ? undefined : 'md'}
      />
    );
  }
  if (chip.type === 'trade') {
    return (
      <TradeBadge
        code={chip.code}
        name={chip.name || chip.label}
        size={size}
      />
    );
  }
  if (chip.type === 'designation') {
    return <InfoBadge color="indigo" label={chip.label} size={size} />;
  }
  if (chip.type === 'designation_keyword') {
    // Trailing "*" signals a substring match (e.g. "Supervisor*" covers
    // any title containing the word).
    return <InfoBadge color="purple" label={chip.label} size={size} />;
  }
  return <InfoBadge color="slate" label={chip.label} size={size} />;
};

const ruleDisplayTitle = (result, rule) =>
  wsqDisplayName(result?.cert_name || rule?.certification_type_details?.name)
  || result?.rule_name
  || rule?.name
  || '';

const ruleDisplaySubtitle = (result, rule) =>
  result?.cert_short_name || rule?.certification_type_details?.short_name || '';

const MetricTile = ({ icon: Icon, color, value, label }) => (
  <div className="metrics-card">
    <div className="metrics-card-content">
      <div className={`metrics-card-icon metrics-card-icon-${color}`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
      </div>
      <div className="metrics-card-text">
        <p className="metrics-card-value">{value}</p>
        <p className="metrics-card-label">{label}</p>
      </div>
    </div>
  </div>
);

// ── Drill-down modal: per-rule employee list ──────────────────────────

const RuleEmployeesModal = ({ ruleResult, onClose }) => {
  React.useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const groups = Object.entries(STATUS_CONFIG)
    .map(([status, cfg]) => ({
      status,
      ...cfg,
      rows: (ruleResult.employees || []).filter((e) => e.status === status),
    }))
    .filter((g) => g.rows.length > 0);

  // Primary library course for this cert (BISH → Supervise Manufacturing…).
  // Nominate uses the first active master; multi-course certs are rare.
  const primaryCourse = (ruleResult.recommended_courses || [])[0] || null;
  const canNominateWorkers = Boolean(primaryCourse?.code);

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="min-w-0">
              <h3 className="modal-title flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="truncate">
                  {wsqDisplayName(ruleResult.cert_name) || ruleResult.rule_name}
                </span>
                <CountBadge
                  count={(ruleResult.employees || []).length}
                  theme="admin"
                  size="md"
                />
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {[
                  ruleResult.cert_short_name,
                  'Gaps & expiring',
                ].filter(Boolean).join(' · ')}
              </p>
              {(ruleResult.recommended_courses || []).length > 0 && (
                <p className="text-xs text-gray-600 mt-1.5 flex items-start gap-1.5">
                  <GraduationCap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                  <span className="min-w-0">
                    {(ruleResult.recommended_courses || []).map((course, i) => (
                      <span key={course.code}>
                        {i > 0 ? '; ' : ''}
                        <span className="font-medium text-gray-800">{course.name}</span>
                        {course.code ? (
                          <span className="text-gray-400"> ({course.code})</span>
                        ) : null}
                      </span>
                    ))}
                  </span>
                </p>
              )}
            </div>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-content overflow-y-auto">
            {groups.length === 0 ? (
              <div className="py-10 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">
                  Everyone in scope holds a valid certificate.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.status} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <div className="px-4 sm:px-5 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {group.label}
                      </span>
                      <CountBadge count={group.rows.length} theme="admin" />
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {group.rows.map((emp) => {
                        // Stacks on mobile so the employee name keeps the full
                        // width inside the narrow modal — otherwise the cert +
                        // expiry + status pills crush it to a single letter.
                        const openRequest = emp.open_course_request;
                        const isWorker = emp.employment_classification === 'worker';
                        const showNominate = (
                          canNominateWorkers
                          && isWorker
                          && !openRequest
                        );
                        const showViewRequest = Boolean(openRequest?.id);
                        const showStaffNote = (
                          canNominateWorkers
                          && !isWorker
                          && !openRequest
                        );

                        return (
                          <li
                            key={`${group.status}-${emp.staff_id}`}
                            className="flex flex-col gap-2 px-4 sm:px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                          >
                            <div className="min-w-0 sm:flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{emp.employee_name}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                                <span>{emp.staff_id}</span>
                                {emp.department ? <span>· {emp.department}</span> : null}
                                {emp.trade &&
                                  !isTradeRedundantWithDepartment(emp.department, null, null, emp.trade) && (
                                  <TradeBadge trade={emp.trade} size="xs" />
                                )}
                                {emp.designation ? <span>· {emp.designation}</span> : null}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 sm:justify-end">
                              {emp.cert_label && <StatusBadge status="assigned" label={emp.cert_label} />}
                              {emp.is_superseded && emp.satisfied_via && (
                                <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                                  via {emp.satisfied_via}
                                </span>
                              )}
                              {emp.expiry_date && (
                                <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                                  {emp.status === 'expired' ? 'Expired' : 'Expires'} {formatDate(emp.expiry_date)}
                                </span>
                              )}
                              <StatusBadge status={group.badge} label={group.label} />
                              {showViewRequest && (
                                <Link
                                  to={buildAdminCourseRequestUrl(openRequest.id)}
                                  onClick={onClose}
                                  className="btn btn-sm btn-admin-outline text-xs"
                                  title={openRequest.request_number || 'Open course request'}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  View request
                                </Link>
                              )}
                              {showNominate && (
                                <Link
                                  to={buildNominateUrl(emp.staff_id, primaryCourse.code)}
                                  onClick={onClose}
                                  className="btn btn-sm btn-admin-outline text-xs"
                                  title={`Nominate for ${primaryCourse.name}`}
                                >
                                  <UserPlus className="w-3.5 h-3.5" />
                                  Nominate
                                </Link>
                              )}
                              {showStaffNote && (
                                <span className="text-xs text-gray-400">Staff · upload cert</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-admin-outline w-full">Close</button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// ── Rule create / edit modal ──────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  certification_type: '',
  applies_to_departments: [],
  applies_to_trades: [],
  applies_to_designations: [],
  applies_to_designation_keywords: [],
  applies_to_classifications: [],
  grace_period_days: 90,
  legal_basis: '',
  notes: '',
  is_active: true,
};

const RuleFormModal = ({ rule, certTypes, departments, trades, designations, onClose }) => {
  const isEdit = Boolean(rule);
  const createMutation = useCreateMandatoryCertRule();
  const updateMutation = useUpdateMandatoryCertRule();
  const [keywordDraft, setKeywordDraft] = useState('');

  const [form, setForm] = useState(() =>
    rule
      ? {
          name: rule.name,
          certification_type: rule.certification_type,
          applies_to_departments: rule.applies_to_departments || [],
          applies_to_trades: rule.applies_to_trades || [],
          applies_to_designations: rule.applies_to_designations || [],
          applies_to_designation_keywords: rule.applies_to_designation_keywords || [],
          applies_to_classifications: rule.applies_to_classifications || [],
          grace_period_days: rule.grace_period_days,
          legal_basis: rule.legal_basis || '',
          notes: rule.notes || '',
          is_active: rule.is_active,
        }
      : EMPTY_FORM
  );

  React.useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const toggleListValue = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const addKeyword = () => {
    const value = keywordDraft.trim();
    setKeywordDraft('');
    if (!value) return;
    setForm((prev) =>
      prev.applies_to_designation_keywords.some((k) => k.toLowerCase() === value.toLowerCase())
        ? prev
        : { ...prev, applies_to_designation_keywords: [...prev.applies_to_designation_keywords, value] }
    );
  };

  const removeKeyword = (value) => {
    setForm((prev) => ({
      ...prev,
      applies_to_designation_keywords: prev.applies_to_designation_keywords.filter((k) => k !== value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    if (!form.certification_type) { toast.error('Select a certification'); return; }

    const payload = {
      ...form,
      grace_period_days: Number(form.grace_period_days) || 0,
    };
    const onSuccess = () => onClose();
    if (isEdit) {
      updateMutation.mutate({ id: rule.id, data: payload }, { onSuccess });
    } else {
      createMutation.mutate(payload, { onSuccess });
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title flex items-center gap-2">
              <Scale className="w-5 h-5 text-red-600" />
              {isEdit ? 'Edit Compliance Rule' : 'New Compliance Rule'}
            </h3>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-content overflow-y-auto space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule name *</label>
                <input
                  type="text"
                  className="input-admin w-full"
                  placeholder="e.g. BCSS — Project, Design & BIM, DfMA"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required certification *</label>
                <select
                  className="select-admin w-full"
                  value={form.certification_type}
                  onChange={(e) => setForm((p) => ({ ...p, certification_type: e.target.value }))}
                >
                  <option value="">Select certification...</option>
                  {certTypes.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.name}{ct.validity_period_months ? ` — renews every ${ct.validity_period_months} months` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departments in scope
                  <span className="text-gray-400 font-normal"> — leave empty for all departments</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto border border-gray-200 rounded-xl p-3">
                  {departments.map((dept) => (
                    <label key={dept.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 accent-red-600 text-red-600 focus:ring-red-500"
                        checked={form.applies_to_departments.includes(dept.id)}
                        onChange={() => toggleListValue('applies_to_departments', dept.id)}
                      />
                      <span className="truncate">{dept.department_name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trades in scope
                  <span className="text-gray-400 font-normal"> — leave empty for all trades</span>
                </label>
                {trades.length === 0 ? (
                  <p className="text-xs text-gray-400">No trade categories configured.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {trades.map((trade) => {
                      const selected = form.applies_to_trades.includes(trade.id);
                      return (
                        <button
                          type="button"
                          key={trade.id}
                          onClick={() => toggleListValue('applies_to_trades', trade.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-red-50 border-red-300 text-red-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {trade.trade_code || trade.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Only workers carry a trade — selecting one narrows the rule to workers in that trade.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Designations in scope
                  <span className="text-gray-400 font-normal"> — leave empty for all designations</span>
                </label>
                {designations.length === 0 ? (
                  <p className="text-xs text-gray-400">No designations in use yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto border border-gray-200 rounded-xl p-3">
                    {designations.map((designation) => {
                      const selected = form.applies_to_designations.includes(designation);
                      return (
                        <button
                          type="button"
                          key={designation}
                          onClick={() => toggleListValue('applies_to_designations', designation)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-red-50 border-red-300 text-red-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {designation}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Matched case-insensitively (exact) — e.g. &quot;WSH Coordinator&quot; covers &quot;WSH COORDINATOR&quot;.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Designation keywords (contains)
                  <span className="text-gray-400 font-normal"> — matches any title containing the word</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.applies_to_designation_keywords.map((kw) => (
                    <span
                      key={kw}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 border border-purple-200 text-purple-700"
                    >
                      {kw}
                      <button
                        type="button"
                        onClick={() => removeKeyword(kw)}
                        className="hover:text-purple-900"
                        aria-label={`Remove ${kw}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {form.applies_to_designation_keywords.length === 0 && (
                    <span className="text-xs text-gray-400">No keywords — leave empty for no keyword restriction.</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input-admin flex-1"
                    placeholder="e.g. Supervisor"
                    value={keywordDraft}
                    onChange={(e) => setKeywordDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }
                    }}
                  />
                  <button type="button" onClick={addKeyword} className="btn btn-admin-outline flex-shrink-0">
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Substring, case-insensitive — &quot;Supervisor&quot; covers &quot;Junior Supervisor&quot;,
                  &quot;Senior Supervisor (Duct)&quot;, etc. Combined with exact designations above (OR).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Classifications in scope
                  <span className="text-gray-400 font-normal"> — leave empty for all classifications</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {CLASSIFICATION_OPTIONS.map((opt) => {
                    const selected = form.applies_to_classifications.includes(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => toggleListValue('applies_to_classifications', opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? 'bg-red-50 border-red-300 text-red-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New-hire grace period (days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="input-admin w-full"
                    value={form.grace_period_days}
                    onChange={(e) => setForm((p) => ({ ...p, grace_period_days: e.target.value }))}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Missing certs aren't flagged until this many days after joining.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Legal basis</label>
                  <input
                    type="text"
                    className="input-admin w-full"
                    placeholder="e.g. WSH (Construction) Regulations"
                    value={form.legal_basis}
                    onChange={(e) => setForm((p) => ({ ...p, legal_basis: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={2}
                  className="textarea-admin w-full"
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 accent-red-600 text-red-600 focus:ring-red-500"
                  checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
                Rule is active (monitored daily and included in the weekly HR digest)
              </label>
            </div>

            <div className="modal-footer flex gap-3">
              <button type="button" onClick={onClose} className="btn btn-admin-outline flex-1">Cancel</button>
              <button type="submit" disabled={saving} className="btn btn-admin-primary flex-1">
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Rule'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

// ── Rule card ─────────────────────────────────────────────────────────

const RuleCard = ({ result, rule, onViewEmployees, onEdit, onDelete }) => {
  const counts = result?.counts || {};
  const nonCompliant = result?.non_compliant ?? 0;
  const expiringSoon = result?.expiring_soon ?? 0;
  const rate = result?.compliance_rate ?? 100;

  const scopeChips = getScopeChips(result, rule);
  const supersededByLabels = (
    result?.superseded_by_labels
    || rule?.certification_type_details?.superseded_by_labels
    || []
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900">{ruleDisplayTitle(result, rule)}</h3>
            {!((result?.is_active) ?? rule?.is_active) && (
              <StatusBadge status="inactive" label="Inactive" />
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{ruleDisplaySubtitle(result, rule)}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {scopeChips.length > 0 ? (
              scopeChips.map((chip) => (
                <ScopeChip key={`${chip.type}-${chip.value || chip.label}`} chip={chip} />
              ))
            ) : (
              <span className="text-xs text-gray-400">Applies to the entire workforce</span>
            )}
          </div>
          {supersededByLabels.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Also satisfied by:{' '}
              <span className="font-medium text-gray-700">{supersededByLabels.join(', ')}</span>
            </p>
          )}
          {(result?.recommended_courses || []).length > 0 && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600">
              <GraduationCap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                {(result.recommended_courses || []).map((course, i) => (
                  <span key={course.code}>
                    {i > 0 ? '; ' : ''}
                    <span className="font-medium text-gray-800">{course.name}</span>
                    {course.code ? (
                      <span className="text-gray-400"> ({course.code})</span>
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result?.legal_basis && (
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <Scale className="w-3 h-3" /> {result.legal_basis}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <IconButton icon={Pencil} onClick={onEdit} variant="default" title="Edit rule" ariaLabel="Edit rule" />
          <IconButton icon={Trash2} onClick={onDelete} variant="danger" title="Delete rule" ariaLabel="Delete rule" />
        </div>
      </div>

      {result && (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-lg font-bold text-gray-900">{result.total_in_scope}</p>
              <p className="text-xs text-gray-500">In scope</p>
            </div>
            <div className={`rounded-xl border p-3 ${nonCompliant > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
              <p className={`text-lg font-bold ${nonCompliant > 0 ? 'text-red-600' : 'text-gray-900'}`}>{nonCompliant}</p>
              <p className="text-xs text-gray-500">Non-compliant</p>
              {(counts.expired > 0 || counts.missing > 0) && (
                <p className="text-[11px] text-red-500 mt-0.5">
                  {counts.expired > 0 && `${counts.expired} expired`}
                  {counts.expired > 0 && counts.missing > 0 && ' · '}
                  {counts.missing > 0 && `${counts.missing} missing`}
                </p>
              )}
            </div>
            <div className={`rounded-xl border p-3 ${expiringSoon > 0 ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}>
              <p className={`text-lg font-bold ${expiringSoon > 0 ? 'text-orange-600' : 'text-gray-900'}`}>{expiringSoon}</p>
              <p className="text-xs text-gray-500">Expiring ≤90 days</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <p className={`text-lg font-bold ${rate >= 100 ? 'text-emerald-600' : rate >= 90 ? 'text-gray-900' : 'text-red-600'}`}>
                {rate}%
              </p>
              <p className="text-xs text-gray-500">Compliance rate</p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="flex-1 mr-4">
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${rate >= 100 ? 'bg-emerald-500' : rate >= 90 ? 'bg-orange-400' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(rate, 100)}%` }}
                />
              </div>
            </div>
            <button
              onClick={onViewEmployees}
              className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
            >
              View employees <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Main tab ──────────────────────────────────────────────────────────

const CertComplianceTab = () => {
  const queryClient = useQueryClient();
  const { data: dashboard, isLoading: dashboardLoading, isFetching } = useMandatoryCertComplianceDashboard();
  const { data: rules = [] } = useMandatoryCertRules();
  const { data: certTypes = [] } = useSafetyCertificationTypes();
  const { data: departments = [] } = useDepartments();
  const { data: trades = [] } = useTradeCategories();
  const { data: designations = [] } = useDistinctDesignations();
  const deleteMutation = useDeleteMandatoryCertRule();

  const [formModal, setFormModal] = useState(null);       // { rule | null }
  const [employeesModal, setEmployeesModal] = useState(null); // rule result

  const ruleById = Object.fromEntries(rules.map((r) => [r.id, r]));
  const results = dashboard?.rules || [];
  const inactiveRules = rules.filter((r) => !r.is_active);

  const handleDelete = (ruleId, ruleName) => {
    if (!window.confirm(`Delete rule "${ruleName}"? Compliance monitoring for it will stop.`)) return;
    deleteMutation.mutate(ruleId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Mandatory Certification Compliance</h3>
          <p className="text-xs sm:text-sm text-gray-500">
            Regulatory rules monitored autonomously — daily employee reminders + weekly HR digest
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <IconButton
            icon={RefreshCw}
            loading={isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['certCompliance'] })}
            title="Refresh"
            ariaLabel="Refresh"
          />
          <button
            onClick={() => setFormModal({ rule: null })}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0"
            aria-label="New Rule"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Rule</span>
          </button>
        </div>
      </div>

      {/* Org-level summary */}
      {dashboard && results.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          <MetricTile
            icon={ShieldAlert}
            color={dashboard.totals.non_compliant > 0 ? 'red' : 'green'}
            value={dashboard.totals.non_compliant}
            label="Non-Compliant"
          />
          <MetricTile
            icon={AlertTriangle}
            color={dashboard.totals.expiring_soon > 0 ? 'orange' : 'green'}
            value={dashboard.totals.expiring_soon}
            label="Expiring ≤90 Days"
          />
        </div>
      )}

      {/* Rule cards */}
      {dashboardLoading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No compliance rules configured</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Define which certifications are mandatory for which departments or classifications.
          </p>
          <button onClick={() => setFormModal({ rule: null })} className="btn btn-admin-primary mt-4">
            <Plus className="w-4 h-4" /> Create your first rule
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {results.map((result) => (
            <RuleCard
              key={result.rule_id}
              result={result}
              rule={ruleById[result.rule_id]}
              onViewEmployees={() => setEmployeesModal(result)}
              onEdit={() => setFormModal({ rule: ruleById[result.rule_id] })}
              onDelete={() => handleDelete(result.rule_id, result.rule_name)}
            />
          ))}
        </div>
      )}

      {/* Inactive rules (not monitored — shown so they can be re-enabled) */}
      {inactiveRules.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Inactive rules — not monitored
          </h3>
          {inactiveRules.map((rule) => (
            <div key={rule.id} className="bg-gray-50 rounded-2xl border border-dashed p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {wsqDisplayName(rule.certification_type_details?.name) || rule.name}
                  </p>
                  <StatusBadge status="inactive" label="Inactive" />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {rule.certification_type_details?.short_name
                    || wsqDisplayName(rule.certification_type_details?.name)}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {(() => {
                    const chips = getScopeChips(null, rule);
                    return chips.length > 0 ? (
                      chips.map((chip) => (
                        <ScopeChip
                          key={`${chip.type}-${chip.value || chip.label}`}
                          chip={chip}
                          size="xs"
                        />
                      ))
                    ) : (
                      <span className="text-[11px] text-gray-400">Applies to the entire workforce</span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <IconButton icon={Pencil} onClick={() => setFormModal({ rule })} variant="default" title="Edit rule" ariaLabel="Edit rule" />
                <IconButton icon={Trash2} onClick={() => handleDelete(rule.id, rule.name)} variant="danger" title="Delete rule" ariaLabel="Delete rule" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {formModal && (
        <RuleFormModal
          rule={formModal.rule}
          certTypes={certTypes}
          departments={departments}
          trades={trades}
          designations={designations}
          onClose={() => setFormModal(null)}
        />
      )}
      {employeesModal && (
        <RuleEmployeesModal ruleResult={employeesModal} onClose={() => setEmployeesModal(null)} />
      )}
    </div>
  );
};

export default CertComplianceTab;
